"use client";

import { TocHeading } from "@/lib/wordpress";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";

interface TableOfContentsProps {
  headings: TocHeading[];
  slug: string;
}

interface TocGroup {
  parent: TocHeading;
  children: TocHeading[];
}

function groupHeadings(headings: TocHeading[]): TocGroup[] {
  const groups: TocGroup[] = [];

  for (const heading of headings) {
    if (heading.level === 2) {
      groups.push({ parent: heading, children: [] });
    } else if (groups.length > 0) {
      groups[groups.length - 1].children.push(heading);
    }
  }

  return groups;
}

function getLinkClasses(isActive: boolean, isChild = false) {
  if (isChild) {
    return isActive
      ? "font-medium text-[#006DFF]"
      : "text-gray-600 hover:text-[#02386D]";
  }

  return isActive
    ? "border-[#006DFF] font-medium text-[#006DFF]"
    : "border-transparent text-gray-600 hover:border-gray-300 hover:text-[#02386D]";
}

const SocialIcons = ({ slug }: { slug: string }) => {
  const shareUrl = `https://www.alexrodriguez.com.au/${slug}`;

  return (
    <div className="flex items-center gap-2">
      {/* Facebook */}
      <Link
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gray-600 transition-colors hover:text-[#1877F2]"
        aria-label="Share on Facebook"
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      </Link>

      {/* Instagram */}
      <Link
        href="https://www.instagram.com/alexrodriguezofficial"
        target="_blank"
        rel="noopener noreferrer"
        className="text-gray-600 transition-colors hover:text-[#E4405F]"
        aria-label="Follow on Instagram"
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
      </Link>

      {/* LinkedIn */}
      <Link
        href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gray-600 transition-colors hover:text-[#0A66C2]"
        aria-label="Share on LinkedIn"
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      </Link>

      {/* X (Twitter) */}
      <Link
        href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gray-600 transition-colors hover:text-black"
        aria-label="Share on X"
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      </Link>
    </div>
  );
};

export default function TableOfContents({ headings, slug }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>("");
  const listRef = useRef<HTMLUListElement>(null);
  const itemRefs = useRef<Record<string, HTMLAnchorElement | null>>({});

  useEffect(() => {
    const container = listRef.current;
    const activeElement = activeId ? itemRefs.current[activeId] : null;
    if (!container || !activeElement) return;

    const containerRect = container.getBoundingClientRect();
    const elementRect = activeElement.getBoundingClientRect();

    const isAbove = elementRect.top < containerRect.top;
    const isBelow = elementRect.bottom > containerRect.bottom;

    if (isAbove || isBelow) {
      const offset =
        elementRect.top -
        containerRect.top -
        container.clientHeight / 2 +
        elementRect.height / 2;

      container.scrollBy({ top: offset, behavior: "smooth" });
    }
  }, [activeId]);

  useEffect(() => {
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-120px 0px -80% 0px",
        threshold: 0,
      }
    );

    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [headings]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setActiveId(id);
    }
  };

  if (headings.length === 0) return null;

  const groups = groupHeadings(headings);

  return (
    <nav className="rounded-xl border border-gray-200 bg-white p-3 lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
      {/* Header with social icons */}
      <div className="mb-2 flex items-center justify-between shrink-0">
        <h2 className="font-crimsonText text-lg font-semibold text-[#02386D]">
          Table of Contents
        </h2>
        {/* <SocialIcons slug={slug} /> */}
      </div>

      {/* TOC list */}
      <ul
        ref={listRef}
        className="max-h-[22vh] lg:max-h-none lg:flex-1 space-y-3 overflow-y-auto custom-sidebar-scrollbar pr-1"
      >
        {groups.map(({ parent, children }) => (
          <li key={parent.id}>
            <a
              ref={(el) => {
                itemRefs.current[parent.id] = el;
              }}
              href={`#${parent.id}`}
              onClick={(e) => handleClick(e, parent.id)}
              className={`block border-l-2 py-0.5 pl-3 font-onest text-sm transition-all duration-200 ${getLinkClasses(
                activeId === parent.id
              )}`}
            >
              {parent.text}
            </a>

            {children.length > 0 && (
              <ul className="ml-3 mt-1 space-y-1 border-l border-[rgba(0,109,255,0.2)] pl-3">
                {children.map((child) => {
                  const isActive = activeId === child.id;

                  return (
                    <li key={child.id} className="relative">
                      <span
                        className={`absolute -left-3 top-1/2 h-px w-3 -translate-y-1/2 ${
                          isActive ? "bg-[#006DFF]" : "bg-[rgba(0,109,255,0.2)]"
                        }`}
                        aria-hidden
                      />
                      <a
                        ref={(el) => {
                          itemRefs.current[child.id] = el;
                        }}
                        href={`#${child.id}`}
                        onClick={(e) => handleClick(e, child.id)}
                        className={`block py-0.5 font-onest text-sm transition-all duration-200 ${getLinkClasses(
                          isActive,
                          true
                        )}`}
                      >
                        {child.text}
                      </a>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}
